// pages/Feed.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { resizeImage } from "../utils/resizeImage";
import MediaGrid from "../components/MediaGrid";
import {
 
  IconMore,
} from "../components/FeedIcons";
import { formatPostTime } from "../utils/formatPostTime";
import FeedMediaComposer from "../components/FeedMediaComposer";
import useMobileComposer from "../hooks/useMobileComposer";
import useDesktopComposer from "../hooks/useDesktopComposer";
import useZoomModal from "../hooks/useZoomModal";
import useBottomSheet from "../hooks/useBottomSheet";
import PostAvatar from "../components/PostAvatar";
import RepostCard from "../components/RepostCard";
import PostActionBar from "../components/PostActionBar";
import { TABS } from "../constants/tabs";
import { usePwaInstall } from "../hooks/usePwaInstall";
import UserInfoOverlay from "../components/UserInfoOverlay";

/* 🔗 LINKIFY — makes links in text blue + clickable.
   Matches https://…, http://…, www.… and bare domains like coins.metisel.com */
const LINK_REGEX = /(?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|co|app|dev|xyz|info|me|ai|gh|uk|us|ng|edu|gov|tv|site|online|store|tech|biz|ly|to|cc)\b(?::\d{2,5})?(?:[/?#][^\s<]*)?/gi;

const splitLinks = (text) => {
  const out = [];
  let last = 0;
  let m;
  LINK_REGEX.lastIndex = 0;
  while ((m = LINK_REGEX.exec(text))) {
    const start = m.index;
    const isBare = !/^(?:https?:\/\/|www\.)/i.test(m[0]);
    // bare domain glued to an email / word (user@site.com) → not a link
    if (isBare && start > 0 && /[@\w.-]/.test(text[start - 1])) continue;
    // keep trailing punctuation out of the link
    const url = m[0].replace(/[.,;:!?)\]}'"]+$/, "");
    if (!url) continue;
    if (start > last) out.push({ text: text.slice(last, start) });
    out.push({ url });
    last = start + url.length;
    LINK_REGEX.lastIndex = last;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};

const LinkifiedText = ({ text }) => {
  if (!text) return null;

  return splitLinks(String(text)).map((part, i) => {
    if (!part.url) return <React.Fragment key={i}>{part.text}</React.Fragment>;

    const href = /^https?:\/\//i.test(part.url) ? part.url : `https://${part.url}`;
    return (
      <a
        key={i}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()} // don't trigger the parent's click
        className="text-blue-500 hover:underline break-all"
      >
        {part.url}
      </a>
    );
  });
};

// "Show more" cut must not land in the middle of a link
const sliceKeepingLinks = (text, limit) => {
  let end = limit;
  let pos = 0;
  for (const p of splitLinks(text)) {
    const len = (p.url ?? p.text).length;
    if (p.url && pos < end && pos + len > end) {
      end = pos + len;
      break;
    }
    pos += len;
  }
  return text.slice(0, end);
};

const IconInteractions = ({ active }) => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* post card */}
    <rect
      x="3"
      y="4"
      width="14"
      height="12"
      rx="2"
      fill={active ? "currentColor" : "none"}
    />
    {/* text lines inside the post */}
    <line
      x1="6"
      y1="8"
      x2="14"
      y2="8"
      stroke={active ? "white" : "currentColor"}
      strokeWidth="1.4"
    />
    <line
      x1="6"
      y1="11.5"
      x2="11"
      y2="11.5"
      stroke={active ? "white" : "currentColor"}
      strokeWidth="1.4"
    />

    {/* reply bubble, overlapping bottom-right corner */}
    <path
      d="M14 13.5c0 2 1.9 3.6 4.3 3.6.4 0 .8 0 1.1-.1L21 18l-.4-1.6a3.9 3.9 0 001.2-2.9c0-2-1.9-3.6-4.3-3.6s-4.3 1.6-4.3 3.6z"
      fill={active ? "currentColor" : "white"}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const IconNotifications = ({ active }) => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill={active ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 01-3.4 0" />
  </svg>
);

export default function Feed({
  mode = "main",
  TABS: TABS_PROP,
  myChatId,
  myUserName,
  users,
   currentUser,
  groups = [],
  groupMembers = {},
  socket,
  uploadImage,
  onFollow,
  onUnfollow,
  feedPosts,
  followingPosts,
  onCreatePost,
  activeTab,
  setActiveTab,
  avatarUrl,
  onLikePost,
  onUploadMedia,
  onAddComment,
  myDisplayName,
  onRepost,
  onViewPost,
  onClearInteraction,
  onDeletePost,
  onEditPost,
  onReportPost,
  feedSocket,
  setDefaultTab,
  onLogout,
  onLoadMoreFeed,
  onLoadMoreGlobal,
  onLoadMoreInteractions,
  feedHasMore,
  feedLoading,
  globalHasMore,
  globalLoading,
  interactionHasMore,
  interactionLoading,
   notificationsBadgeCount = 0,
   interactionsBadgeCount = 0,
  onSupportPost,
  
  uswdWallet,
}) {
  const T = TABS_PROP || TABS;

 useEffect(() => {
  const handleBeforeUnload = () => {
    sessionStorage.setItem("feed_hard_reload", "1");
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, []);
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(mode === "main" ? "forYou" : null);
  const [text, setText] = useState("");
  const [showComposer, setShowComposer] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [isPosting, setIsPosting] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [mutedVideos, setMutedVideos] = useState({});
  const [zoomType, setZoomType] = useState("image");
  const [uploadProgress, setUploadProgress] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});
  const [openComments, setOpenComments] = useState({});
  const [replyToPost, setReplyToPost] = useState(null);
  const [isReplyMode, setIsReplyMode] = useState(false);
  const [quotePost, setQuotePost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const desktopHeaderMenuRef = useRef(null);
  const mobileHeaderMenuRef = useRef(null);
   const { canInstall, isIOSInstallable, handleInstall } = usePwaInstall();
  const [showIOSInstallHelp, setShowIOSInstallHelp] = useState(false);

  const [menu, setMenu] = useState({ open: false, post: null, type: null });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  // ── SUPPORT MODAL STATE ──────────────────────────────────────────────────
  const [supportModal, setSupportModal] = useState({ open: false, post: null });
  const [supportAmount, setSupportAmount] = useState("");
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState("");
   const [supportResult, setSupportResult] = useState(null);

   const NETWORK_FEE_USWD = 0.01;

  // ── EARNINGS MODAL STATE (post author view) ─────────────────────────────
  const [earningsModal, setEarningsModal] = useState({ open: false, post: null });

  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const headerHideThreshold = 20;
  const scrollTickingRef = useRef(false);

  /* ── NEW POST QUEUE ── */
  const shownPostIdsRef = useRef(null);
  const newestKnownAtRef = useRef(null);

  const clearBadgeStorage = () => {
    sessionStorage.removeItem("feed_pending_posts");
    sessionStorage.removeItem("feed_snapshot");
  };

  const [pendingPosts, setPendingPosts] = useState(() => {
    try {
      const wasReload = sessionStorage.getItem("feed_hard_reload");
      if (wasReload) {
        sessionStorage.removeItem("feed_hard_reload");
        sessionStorage.removeItem("feed_pending_posts");
        sessionStorage.removeItem("feed_snapshot");
        return [];
      }
      const saved = sessionStorage.getItem("feed_pending_posts");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        clearBadgeStorage();
        setPendingPosts([]);
        shownPostIdsRef.current = null;
        newestKnownAtRef.current = null;
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    if (pendingPosts.length === 0) {
      sessionStorage.removeItem("feed_pending_posts");
    } else {
      sessionStorage.setItem("feed_pending_posts", JSON.stringify(pendingPosts));
    }
  }, [pendingPosts]);

  const scrollContainerRef = useRef(null);
  const savedPostIdRef = useRef(null);

  // ── UNLOCK REAL DOCUMENT SCROLL ON THE MAIN FEED ────────────────────────
  // index.html pins <body> to position:fixed/overflow:hidden globally to
  // stop iOS rubber-banding on every page. That also blocks the browser's
  // own URL-bar hide/show, which only reacts to real document scroll. This
  // adds a scoped class only while the main Feed tab is mounted, and always
  // removes it on cleanup so every other page keeps the original lock.
 useEffect(() => {
  if (mode !== "main") return;
  const mq = window.matchMedia("(max-width: 767px)");
  const apply = () => {
    document.documentElement.classList.toggle("feed-scroll-active", mq.matches);
  };
  apply();
  mq.addEventListener("change", apply);
  return () => {
    mq.removeEventListener("change", apply);
    document.documentElement.classList.remove("feed-scroll-active");
  };
}, [mode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const desktopInside = desktopHeaderMenuRef.current?.contains(e.target);
      const mobileInside  = mobileHeaderMenuRef.current?.contains(e.target);
      if (!desktopInside && !mobileInside) setHeaderMenuOpen(false);
    };
    if (headerMenuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [headerMenuOpen]);

  // ── SCROLL HANDLER ───────────────────────────────────────────────────────
  useEffect(() => {
    const isMainFeed = mode === "main";
    const container = scrollContainerRef.current;
    if (!isMainFeed && !container) return;

    const LOAD_MORE_THRESHOLD = 500;

    const getScrollY = () => (isMainFeed ? window.scrollY : container.scrollTop);
    const getNearBottom = () =>
      isMainFeed
        ? window.scrollY + window.innerHeight >=
          document.documentElement.scrollHeight - LOAD_MORE_THRESHOLD
        : container.scrollTop + container.clientHeight >=
          container.scrollHeight - LOAD_MORE_THRESHOLD;

    const handleScroll = () => {
      const currentY = getScrollY();

      const delta = currentY - lastScrollYRef.current;
      if (Math.abs(delta) >= headerHideThreshold) {
        setHeaderVisible(delta < 0 || currentY < 10);
        lastScrollYRef.current = currentY;
      }

      if (!getNearBottom()) return;

      if (mode === "main" && tab === "following") {
        if (!feedLoading && feedHasMore && onLoadMoreFeed) onLoadMoreFeed();
        return;
      }

      if (mode === "main" && tab === "forYou") {
        if (!globalLoading && globalHasMore && onLoadMoreGlobal) onLoadMoreGlobal();
        return;
      }

      if (mode === "commented" || activeTab === TABS.INTERACTIONS) {
        if (!interactionLoading && interactionHasMore && onLoadMoreInteractions)
          onLoadMoreInteractions();
      }
    };

    const target = isMainFeed ? window : container;
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, [
    tab,
    mode,
    activeTab,
    feedLoading,
    feedHasMore,
    onLoadMoreFeed,
    globalLoading,
    globalHasMore,
    onLoadMoreGlobal,
      interactionLoading,
    interactionHasMore,
    onLoadMoreInteractions,
  ]);


  const postsToRender =
    mode === "commented"
      ? feedPosts || []
      : tab === "following"
        ? followingPosts || []
        : feedPosts || [];

  useEffect(() => {
    if (shownPostIdsRef.current === null && postsToRender.length > 0) {
      shownPostIdsRef.current = new Set(postsToRender.map((p) => String(p.postId)));
      const maxTs = postsToRender.reduce((max, p) => {
        const t = p.createdAt ? new Date(p.createdAt).getTime() : 0;
        return t > max ? t : max;
      }, 0);
      newestKnownAtRef.current = maxTs;
      setPendingPosts([]);
      sessionStorage.removeItem("feed_pending_posts");
    }
  }, [postsToRender]);

  useEffect(() => {
    try {
      const snapshot = {
        shown:  Array.from(shownPostIdsRef.current || []),
        newest: newestKnownAtRef.current || 0,
      };
      sessionStorage.setItem("feed_snapshot", JSON.stringify(snapshot));
    } catch {}
  }, [feedPosts]);

  useEffect(() => {
    if (mode !== "main") return;
    if (tab !== "forYou") return;
    if (shownPostIdsRef.current === null) return;
    if (newestKnownAtRef.current === null) return;
    const cutoff   = newestKnownAtRef.current;
    const incoming = (feedPosts || []).filter((p) => {
      if (shownPostIdsRef.current.has(String(p.postId))) return false;
      if (String(p.author?.chatId) === String(myChatId)) return false;
      const postTs = p.createdAt ? new Date(p.createdAt).getTime() : 0;
      return postTs > cutoff;
    });
    if (incoming.length === 0) return;
    setPendingPosts((prev) => {
      const prevIds   = new Set(prev.map((p) => String(p.postId)));
      const truly_new = incoming.filter((p) => !prevIds.has(String(p.postId)));
      if (truly_new.length === 0) return prev;
      return [...truly_new, ...prev];
    });
  }, [feedPosts, mode, tab, myChatId]);

  useEffect(() => {
    if (tab !== "forYou") return;
    shownPostIdsRef.current  = null;
    newestKnownAtRef.current = null;
  }, [tab]);

  const showNewPosts = useCallback(() => {
    if (pendingPosts.length === 0) return;
    pendingPosts.forEach((p) => shownPostIdsRef.current?.add(String(p.postId)));
    setPendingPosts([]);
    sessionStorage.removeItem("feed_pending_posts");
    if (mode === "main") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const container = scrollContainerRef.current || document.getElementById("feed-scroll-container");
      if (container) container.scrollTo({ top: 0, behavior: "smooth" });
    }
    setHeaderVisible(true);
  }, [pendingPosts, mode]);

  const pendingCount = pendingPosts.length;
  const badgeLabel   =
    pendingCount > 100
      ? "100+ new posts"
      : `${pendingCount} new post${pendingCount !== 1 ? "s" : ""}`;
const visiblePosts =
  mode === "commented"
    ? feedPosts || []
    : tab === "following"
      ? followingPosts || []
      : (() => {
          const base = feedPosts || [];
          const shownIds = shownPostIdsRef.current;
          if (!shownIds) return base;
          const pendingIds = new Set(pendingPosts.map((p) => String(p.postId)));
          return base.filter((p) => !pendingIds.has(String(p.postId)));
        })();

useEffect(() => {
  // Don't restore while we're still on the Post page.
  if (location.state?.view === "post") return;

  const targetId = sessionStorage.getItem("feed_return_post_id");
  const savedY = sessionStorage.getItem("feed_return_scroll_y");

  if (!targetId && !savedY) return;

  let attempts = 0;

  const restore = () => {
    const el = document.querySelector(`[data-postid="${targetId}"]`);

    if (!el) {
      if (attempts++ < 60) requestAnimationFrame(restore);
      return;
    }

       if (targetId) {
      el.scrollIntoView({
        block: "center",
        behavior: "auto",
      });
    } else if (savedY !== null) {
      window.scrollTo({
        top: Number(savedY),
        behavior: "auto",
      });
    }

    sessionStorage.removeItem("feed_return_post_id");
    sessionStorage.removeItem("feed_return_scroll_y");
  };

  requestAnimationFrame(restore);
}, [location.state, mode, visiblePosts.length]);

const toggleExpanded = (postId, e) => {
    e.stopPropagation();
    setExpandedPosts((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handlePost = async () => {
    if (isPosting) return;
    if (!text.trim() && mediaFiles.length === 0 && !quotePost && !editingPost) return;
    setIsPosting(true);
    try {
      if (editingPost) {
        onEditPost({ postId: editingPost.postId, text: text.trim() || null });
        setEditingPost(null);
        setText("");
        setMediaFiles([]);
        setMediaPreview([]);
        setShowComposer(false);
        return;
      }
      let uploadedMedia = [];
      if (mediaFiles.length > 0) {
        uploadedMedia = await onUploadMedia(mediaFiles, (fileName, percent) => {
          setUploadProgress((prev) => ({ ...prev, [fileName]: percent }));
        });
        if (!Array.isArray(uploadedMedia)) throw new Error("Media upload failed");
      }
    if (quotePost) {
  onRepost({
    postId: quotePost.postId,
    chatId: myChatId,
    text: text?.trim() || null,
    originalPost: quotePost,
    authorUsername: myUserName,       // ✅ NEW
    authorDisplayName: myDisplayName,
  });
} else {
  onCreatePost({ chatId: myChatId, authorUsername: myUserName, authorDisplayName: myDisplayName, text: text?.trim() || null, media: uploadedMedia });
}
      setText("");
      setMediaFiles([]);
      setMediaPreview([]);
      setQuotePost(null);
      setShowComposer(false);
    } catch (err) {
 
    } finally {
      setIsPosting(false);
    }
  };

  const handleCommentChange = (postId, value) => {
    setCommentDrafts((prev) => ({ ...prev, [postId]: value }));
  };

  const handleSendComment = (postId) => {
    if (!text.trim()) return;
    onAddComment({ postId, chatId: myChatId, text, authorDisplayName: myDisplayName });
    setText("");
  };

  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remainingSlots = 4 - mediaFiles.length;
    if (remainingSlots <= 0) return;
    const limitedFiles   = files.slice(0, remainingSlots);
    const processedFiles = [];
    const previews       = [];
    for (const file of limitedFiles) {
      if (file.type.startsWith("image/")) {
        const resized = await resizeImage(file, { maxSize: 1280, quality: 0.8, mimeType: "image/jpeg" });
        processedFiles.push(resized);
        previews.push({ url: URL.createObjectURL(resized), type: "image" });
      } else {
        processedFiles.push(file);
        previews.push({ url: URL.createObjectURL(file), type: "video" });
      }
    }
    setMediaFiles((prev)   => [...prev, ...processedFiles]);
    setMediaPreview((prev) => [...prev, ...previews]);
  };

  // ── VIEW TRACKING ────────────────────────────────────────────────────────
  const observerRef    = useRef(null);
  const viewedPostsRef = useRef(new Set());

  useEffect(() => {
    if (!onViewPost) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const postId = entry.target.dataset.postid;
          if (viewedPostsRef.current.has(postId)) return;
          viewedPostsRef.current.add(postId);
          setTimeout(() => { onViewPost({ postId }); }, 650);
          observerRef.current.unobserve(entry.target);
        });
      },
      { threshold: 0.7 }
    );
    return () => observerRef.current?.disconnect();
  }, [onViewPost]);

  const scrollToTop = useCallback(() => {
    if (mode === "main") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const container = scrollContainerRef.current || document.getElementById("feed-scroll-container");
      if (container && container.scrollTop > 0) {
        container.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    if (pendingPosts.length > 0) {
      pendingPosts.forEach((p) => shownPostIdsRef.current?.add(String(p.postId)));
      setPendingPosts([]);
    }
    setHeaderVisible(true);
  }, [pendingPosts, mode]);

  // ── SUPPORT MODAL HANDLERS ───────────────────────────────────────────────

 const handleOpenSupportModal = useCallback(({ post }) => {
    setSupportModal({ open: true, post });
    setSupportAmount("");
    setSupportError("");
    setSupportResult(null); // NEW
  }, []);

  const handleCloseSupportModal = useCallback(() => {
    if (supportLoading) return;
    setSupportModal({ open: false, post: null });
    setSupportAmount("");
    setSupportError("");
    setSupportResult(null); // NEW
  }, [supportLoading]);

  // ── SUPPORT RESULT LISTENER ──────────────────────────────────────────────
useEffect(() => {
    if (!feedSocket?.onSupportPostResult) return;
    const unsubscribe = feedSocket.onSupportPostResult((result) => {
      setSupportLoading(false);
      if (!result?.success) {
        toast?.error?.(result?.error || "Failed to send support");
        setSupportError(result?.error || "Failed to send support");
        return;
      }
      setSupportResult(result.data); // { amount, fee, total, ... }
    });
    return unsubscribe;
  }, [feedSocket]);
  const handleSupportSubmit = async () => {
    const amount = parseFloat(supportAmount);

    if (!amount || amount <= 0) {
      setSupportError("Enter a valid amount");
      return;
    }

    const post = supportModal.post;
    if (!post?.postId) {
      setSupportError("Post not available");
      return;
    }

    setSupportLoading(true);
    setSupportError("");
    setSupportResult(null); // NEW

    try {
      const timestamp = Date.now();

      onSupportPost?.({
        postId: post.postId,
        amount,
        timestamp,
      });
      // NOTE: modal stays open (loading) until onSupportPostResult fires
    } catch (err) {
      setSupportError(err?.message || "Failed to send support");
      setSupportLoading(false); // NEW: only reset loading on early/local failure
    }
  };

  // ── EARNINGS MODAL HANDLERS (post author view) ───────────────────────────

  const handleOpenEarningsModal = useCallback(({ post }) => {
    setEarningsModal({ open: true, post });
  }, []);

  const handleCloseEarningsModal = useCallback(() => {
    setEarningsModal({ open: false, post: null });
  }, []);

  const storedDefault = localStorage.getItem("defaultTab") || T.FEED;

  /* ── Dropdown menu shared JSX ── */
  const headerDropdown = (
    <div className="absolute right-0 top-10 w-56 rounded-2xl shadow-xl z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 overflow-hidden">
     <button
  onClick={() => {
    setHeaderMenuOpen(false);
    setShowProfile(true);
  }}
  className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
>
  <span>👤</span>
  <span>Profile</span>
</button>

<div className="h-px bg-neutral-200 dark:bg-neutral-700" />

<button
  onClick={() => {
    setHeaderMenuOpen(false);
    setDefaultTab(storedDefault === T.FEED ? T.CHATS : T.FEED);
  }}
  className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
>
  <span>🏠</span>
  <span>{storedDefault === T.FEED ? "Set Chats as home" : "Set Feed as home"}</span>
</button>
      <div className="h-px bg-neutral-200 dark:bg-neutral-700" />
     
      {canInstall && (
        <>
          <button
            onClick={() => { setHeaderMenuOpen(false); handleInstall(); }}
            className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
          >
            <span>📲</span><span>Install app</span>
          </button>
          <div className="h-px bg-neutral-200 dark:bg-neutral-700" />
        </>
      )}
      {!canInstall && isIOSInstallable && (
        <>
          <button
            onClick={() => { setHeaderMenuOpen(false); setShowIOSInstallHelp(true); }}
            className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
          >
            <span>📲</span><span>Install app</span>
          </button>
          <div className="h-px bg-neutral-200 dark:bg-neutral-700" />
        </>
      )}
          <button
        onClick={() => {
          setHeaderMenuOpen(false);
          setActiveTab?.(T.SETTINGS);
        }}
        className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
      >
        <span>⚙️</span>
        <span>Settings</span>
      </button>

      <div className="h-px bg-neutral-200 dark:bg-neutral-700" />

      <button
        onClick={() => {
          setHeaderMenuOpen(false);
          setActiveTab?.(T.ABOUT);
        }}
        className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
      >
        <span>ℹ️</span>
        <span>About</span>
      </button>

      <div className="h-px bg-neutral-200 dark:bg-neutral-700" />

      <button
        onClick={() => {
          setHeaderMenuOpen(false);
          setActiveTab?.(T.HELP);
        }}
        className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
      >
        <span>❓</span>
        <span>Help Center</span>
      </button>

      <div className="h-px bg-neutral-200 dark:bg-neutral-700" />

      <button
        onClick={() => {
          setHeaderMenuOpen(false);
          window.location.reload();
        }}

        className="w-full px-4 py-3 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-3"
      >
        <span>🔄</span><span>Refresh</span>
      </button>
      {onLogout && (
        <>
          <div className="h-px bg-neutral-200 dark:bg-neutral-700" />
          <button
            onClick={() => { setHeaderMenuOpen(false); onLogout(); }}
            className="w-full px-4 py-3 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-3"
          >
            <span>🚪</span><span>Log out</span>
          </button>
        </>
      )}
    </div>
  );

  const headerSpacerHeight = mode === "main" ? "108px" : "56px";
  const isMainFeed = mode === "main";
return (
  <div
    id="feed-scroll-container"
    ref={scrollContainerRef}
    className={
      isMainFeed
     ? "relative flex flex-col w-full min-h-[100dvh] md:h-[100dvh] md:overflow-y-auto md:overflow-x-hidden bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
        : "relative flex flex-col h-[100dvh] w-full overflow-y-auto overflow-x-hidden bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
    }
  >
      {/* ── HEADER ── */}
      <div
        className={[
          "sticky top-0 left-0 right-0 z-30",
          "transition-transform duration-300 ease-in-out",
          headerVisible ? "translate-y-0" : "-translate-y-full",
          "bg-white/95 dark:bg-neutral-800/95 backdrop-blur-sm",
          "border-b border-neutral-200 dark:border-neutral-700",
        ].join(" ")}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
     
<div className="relative flex items-center px-4 pt-3 pb-2 min-w-0">
 
{mode === "commented" && (
  <button
    onClick={() => setActiveTab?.(T.FEED)}
    className="mr-3 flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
    aria-label="Back"
  >
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  </button>
)}
  <div className="text-xl font-bold text-green-600 select-none truncate flex-shrink-0">
    {mode === "commented" ? "Interactions" : "Metisel"}
  </div>

   {mode === "main" && (
    <>
      <div className="ml-auto flex items-center gap-2 flex-shrink-0">

                    {/* INTERACTIONS ICON */}
        <div className="relative">
          {interactionsBadgeCount > 0 && (
           <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-green-500 px-1 text-[10px] font-bold text-white">
              {interactionsBadgeCount > 99 ? "99+" : interactionsBadgeCount}
            </span>
          )}
          <button
            onClick={() => setActiveTab?.(T.INTERACTIONS)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-900 dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700 active:bg-neutral-300 dark:active:bg-neutral-600 touch-manipulation"
            aria-label="Interactions"
            title="Interactions"
          >
            <IconInteractions active={activeTab === T.INTERACTIONS} />
          </button>
        </div>

              {/* NOTIFICATIONS ICON */}
        <div className="relative">
          {notificationsBadgeCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {notificationsBadgeCount > 99 ? "99+" : notificationsBadgeCount}
            </span>
          )}
          <button
            onClick={() => setActiveTab?.(T.NOTIFICATIONS)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-900 dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700 active:bg-neutral-300 dark:active:bg-neutral-600 touch-manipulation"
            aria-label="Notifications"
            title="Notifications"
          >
            <IconNotifications active={activeTab === T.NOTIFICATIONS} />
          </button>
        </div>

        {/* MOBILE — profile image */}
        {setDefaultTab && (
          <div
            className="md:hidden relative"
            ref={mobileHeaderMenuRef}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setHeaderMenuOpen((s) => !s);
              }}
              className="block touch-manipulation"
              title="Options"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Me"
                  className="w-8 h-8 rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-600 flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-sm flex-shrink-0">
                  👤
                </div>
              )}
            </button>

            {headerMenuOpen && (
              <div className="absolute right-0 top-10 w-56 rounded-2xl shadow-xl z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                {headerDropdown.props.children}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )}
</div>
        {mode === "main" && (
          <div className="flex items-center overflow-visible">
            <button
              onClick={() => setTab("forYou")}
              className={`relative flex-1 py-3 font-semibold text-center text-sm touch-manipulation transition-colors ${
                tab === "forYou" ? "text-blue-500" : "text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              }`}
            >
              <span className="relative inline-block">
                For you
                {tab === "forYou" && (
                  <span className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] w-full h-[2px] bg-blue-500 rounded-full" />
                )}
              </span>
            </button>

            <button
              onClick={() => setTab("following")}
              className={`relative flex-1 py-3 font-semibold text-center text-sm touch-manipulation transition-colors ${
                tab === "following" ? "text-blue-500" : "text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              }`}
            >
              <span className="relative inline-block">
                Following
                {tab === "following" && (
                  <span className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] w-full h-[2px] bg-blue-500 rounded-full" />
                )}
              </span>
            </button>
          </div>
        )}
      </div>

      <div
        className="md:hidden flex-shrink-0"
        style={{ height: headerVisible ? "0px" : headerSpacerHeight }}
        aria-hidden="true"
      />

      {/* NEW POSTS BADGE */}
      {mode === "main" && tab === "forYou" && pendingCount > 0 && (
        <div className="sticky z-50 pointer-events-none flex justify-center" style={{ top: 8 }}>
          <button
            onClick={showNewPosts}
            className="pointer-events-auto flex items-center gap-1.5 px-4 py-2 bg-blue-500 hover:bg-blue-600 active:scale-95 text-white text-xs font-semibold rounded-full shadow-lg transition-all duration-150 touch-manipulation"
          >
            <span>↑</span>
            <span>{badgeLabel}</span>
          </button>
        </div>
      )}

      {/* SCROLL AREA */}
      <div className="flex-1 min-w-0 w-full">
        {/* CREATE POST — DESKTOP / TABLET ONLY */}
        {mode === "main" && (
          <div className="hidden md:flex gap-3 p-4 border-b border-neutral-200 dark:border-neutral-700">
            <div className="w-10 h-10 shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Me" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center">👤</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <textarea
                id="feed-post-textarea"
                name="feed-post"
                autoComplete="off"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What's happening?"
                rows={2}
                className="w-full resize-none bg-transparent outline-none text-base"
              />
              <FeedMediaComposer
                mediaFiles={mediaFiles}
                mediaPreview={mediaPreview}
                uploadProgress={uploadProgress}
                isPosting={isPosting}
                onSelectMedia={handleMediaSelect}
                showProgress
              />
              {quotePost && (
                <div className="mb-4 rounded-xl border border-neutral-200 dark:border-neutral-700 p-3">
                  <div className="text-xs text-neutral-500 mb-1">🔁 Quoting @{quotePost.author?.username}</div>
                  {quotePost.text && <div className="text-sm whitespace-pre-wrap">{quotePost.text}</div>}
                  {quotePost.media?.length > 0 && <MediaGrid media={quotePost.media} />}
                </div>
              )}
              <div className="flex justify-end mt-2">
                <button
                  onClick={handlePost}
                  disabled={isPosting}
                  className="px-5 py-1.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 disabled:opacity-60 text-white font-semibold text-sm transition-colors"
                >
                  {editingPost ? "Update" : "Post"}
                </button>
              </div>
            </div>
          </div>
        )}

               {/* NO INTERACTIONS — empty state */}
        {mode === "commented" && !interactionLoading && visiblePosts.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center px-6 py-16 text-neutral-400 dark:text-neutral-500">
            <span className="text-4xl mb-3" aria-hidden="true">💬</span>
            <p className="text-sm font-medium text-neutral-600 dark:text-neutral-300">
              You have no interactions
            </p>
            <p className="text-xs mt-1">
              Comments and replies on posts will show up here.
            </p>
          </div>
        )}

        {/* FEED LIST */}
        {visiblePosts.map((post) => (
          <div
            key={post.postId}
            data-postid={post.postId}
            ref={(el) => {
              if (!el || !observerRef.current) return;
              observerRef.current.observe(el);
            }}
            className={[
            "px-3 py-1.5 sm:px-4 sm:py-2 min-w-0 w-full",
              "border-b border-neutral-200 dark:border-neutral-700",
              post.isDeleted
                ? "cursor-default"
                : "cursor-pointer active:bg-neutral-50 dark:active:bg-neutral-700/40 transition-colors duration-100",
            ].join(" ")}
               onClick={() => {
  if (post.isDeleted) return;
  if (mode === "commented") onClearInteraction?.(post.postId);

  // Persist across Feed unmount
  sessionStorage.setItem("feed_return_post_id", String(post.postId));
  sessionStorage.setItem("feed_return_scroll_y", String(window.scrollY));

  navigate(location.pathname, {
    state: {
      view: "post",
      post,
      previewCommentId: post.latestCommentPreview?.commentId || null,
      fromScrollY: window.scrollY,
    },
  });
}}       
          >
            {post.isDeleted ? (
              <div className="flex gap-3 opacity-70 min-w-0">
                <PostAvatar post={post} users={users} myChatId={myChatId} avatarUrl={avatarUrl} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="font-semibold text-neutral-800 dark:text-neutral-200 truncate">
                      {post.author?.displayName || post.author?.username || "Unknown"}
                    </span>
                    <span className="text-neutral-400 text-xs">{formatPostTime(post.createdAt)}</span>
                  </div>
                  <div className="mt-1 text-sm text-neutral-400 italic">This post was deleted.</div>
                </div>
              </div>
            ) : (
             <div className="flex gap-3 min-w-0">
 <div className="shrink-0">
  <PostAvatar
    post={post}
    users={users}
    myChatId={myChatId}
    avatarUrl={avatarUrl}
    onClick={(e) => {
      e.stopPropagation();
      setSelectedUserId(post.author?.chatId);
    }}
  />
</div>

<div className="flex-1 min-w-0">
   <div className="flex items-start justify-between gap-1 min-w-0 leading-tight">
    <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
      <span
        onClick={(e) => {
          e.stopPropagation();
          setSelectedUserId(post.author?.chatId);
        }}
        className="font-semibold text-neutral-800 dark:text-neutral-200 truncate shrink text-sm cursor-pointer hover:underline"
      >
        {post.author?.displayName || post.author?.username || "Unknown"}
      </span>

      <span
        onClick={(e) => {
          e.stopPropagation();
          setSelectedUserId(post.author?.chatId);
        }}
        className="text-neutral-400 text-sm truncate shrink min-w-0 cursor-pointer hover:underline"
      >
        @{post.author?.username}
      </span>

      <span className="text-neutral-400 text-sm flex-shrink-0">·</span>

      <span className="text-neutral-400 text-xs whitespace-nowrap flex-shrink-0">
        {formatPostTime(post.createdAt)}
      </span>

      {post.isEdited && !post.isDeleted && (
        <span className="text-neutral-400 text-xs flex-shrink-0">
          (edited)
        </span>
      )}
    </div>

    <button
      onClick={(e) => {
        e.stopPropagation();
        const isAuthor = String(post.author?.chatId) === String(myChatId);
        setMenu({
          open: true,
          post,
          type: isAuthor ? "post-actions" : "post-report",
        });
      }}
           className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 -mr-1 -my-1 touch-manipulation transition-colors"
      style={{ minWidth: 24, minHeight: 24 }}
    >
      <IconMore />
    </button>
  </div>
                 {/* COMMENT REPOST */}
{post.isCommentRepost && post.originalComment && (
  <div className="mt-2 min-w-0">
    {post.isQuote && post.text && (() => {
      const LIMIT      = 280;
      const isExpanded = expandedPosts[post.postId];
      const isLong     = post.text.length > LIMIT;
      const displayText = isExpanded || !isLong ? post.text : sliceKeepingLinks(post.text, LIMIT) + "...";
      return (
        <>
<div className="text-[14px] whitespace-pre-wrap break-words leading-snug"><LinkifiedText text={displayText} /></div>
          {isLong && (
            <button onClick={(e) => toggleExpanded(post.postId, e)} className="mt-1 text-blue-500 font-medium text-sm touch-manipulation">
              {isExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </>
      );
    })()}
    <div className="mt-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 p-3 min-w-0">
      <div className="text-xs text-neutral-500 mb-1.5">
        🔁 {post.isQuote ? `${post.author?.username} quoted a comment` : `${post.author?.username} reposted a comment`}
      </div>
      <div className="font-semibold text-sm truncate">{post.originalComment.author?.username}</div>
      {post.originalComment.text && (
        <div className="text-sm whitespace-pre-wrap break-words"><LinkifiedText text={post.originalComment.text} /></div>
      )}
      {/* ✅ NEW — render the original comment's media */}
      {post.originalComment.media?.length > 0 && (
        <div className="mt-2">
          <MediaGrid
            media={post.originalComment.media}
            onClick={(m) => { setZoomSrc(m.url); setZoomType(m.type); setZoomOpen(true); }}
          />
        </div>
      )}
    </div>
  </div>
)}

                  {/* POST TEXT */}
                  {post.text && !post.isCommentRepost && (() => {
                    const LIMIT       = 280;
                    const isExpanded  = expandedPosts[post.postId];
                    const isLong      = post.text.length > LIMIT;
                    const displayText = isExpanded || !isLong ? post.text : sliceKeepingLinks(post.text, LIMIT) + "...";
                    return (
                                         <div className="mt-0 min-w-0">
                   <div className="text-[14px] whitespace-pre-wrap break-words leading-snug"><LinkifiedText text={displayText} /></div>
                        {isLong && (
                          <button onClick={(e) => toggleExpanded(post.postId, e)} className="mt-1 text-blue-500 font-medium text-sm touch-manipulation">
                            {isExpanded ? "Show less" : "Show more"}
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* MEDIA */}
                  {post.media?.length > 0 && (
                    <div className="mt-2 min-w-0 w-full">
                      <MediaGrid
                        media={post.media}
                        onClick={(m) => { setZoomSrc(m.url); setZoomType(m.type); setZoomOpen(true); }}
                      />
                    </div>
                  )}

                  <RepostCard
                    post={post}
                    navigate={navigate}
                    location={location}
                    setZoomSrc={setZoomSrc}
                    setZoomType={setZoomType}
                    setZoomOpen={setZoomOpen}
                  />

                  {/* INTERACTION PREVIEW */}
                  {mode === "commented" && post.latestCommentPreview && (
                    <div className="mt-3 flex justify-between items-start gap-2 min-w-0">
                      <div className="flex-1 min-w-0">
                        {(() => {
                          const LIMIT       = 40;
                          const previewText = post.latestCommentPreview.text || "";
                          const isLong      = previewText.length > LIMIT;
                          const displayText = isLong ? previewText.slice(0, LIMIT).trimEnd() + "...." : previewText;
                          return (
                            <div className="px-3 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-700 text-sm min-w-0">
                              <span className="font-semibold">@{post.latestCommentPreview.username}</span>{" "}
                              <span className="break-words">{displayText}</span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0">
                        {post.latestCommentPreview.time && (
                          <div className="text-xs text-neutral-400 whitespace-nowrap">{formatPostTime(post.latestCommentPreview.time)}</div>
                        )}
                        {post.latestCommentPreview.newCount > 0 && (
                          <div className="mt-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-500 text-white text-[11px] font-semibold">
                            {post.latestCommentPreview.newCount > 99 ? "99+" : post.latestCommentPreview.newCount}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <PostActionBar
                    post={post}
                    navigate={navigate}
                    location={location}
                    setRepostMenu={(payload) => setMenu({ ...payload, type: "repost" })}
                    onLikePost={onLikePost}
                    myChatId={myChatId}
                    setMenu={setMenu}
                    onSupportPost={onSupportPost ? handleOpenSupportModal : undefined}
                    onViewEarnings={handleOpenEarningsModal}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Bottom safe-area padding for iOS home indicator + tab bar */}
        <div
          className="md:hidden flex-shrink-0"
          style={{ height: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}
          aria-hidden="true"
        />
      </div>

      {/* SCROLL TO TOP — mobile only */}
      {mode === "main" && tab === "forYou" && (
        <button
          onClick={scrollToTop}
          title={pendingCount > 0 ? badgeLabel : "Back to top"}
          className="md:hidden fixed z-40 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 active:scale-95 text-neutral-800 dark:text-neutral-200 text-base font-bold rounded-full shadow-lg border border-neutral-200 dark:border-neutral-600 transition-all duration-150 touch-manipulation"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)", left: 16 }}
        >
          ↑
        </button>
      )}

      {/* FLOATING COMPOSE — mobile/tablet */}
      {mode === "main" && (
        <button
          onClick={() => setShowComposer(true)}
          className="md:hidden fixed z-40 w-14 h-14 rounded-full bg-blue-500 hover:bg-blue-600 active:scale-95 text-white text-2xl flex items-center justify-center shadow-xl transition-all duration-150 touch-manipulation"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)", right: 16 }}
          aria-label="Create post"
        >
          ✎
        </button>
      )}

      {useMobileComposer({
        showComposer, setShowComposer, avatarUrl, text, setText,
        mediaFiles, mediaPreview, uploadProgress, isPosting,
        handleMediaSelect, handlePost, quotePost, setQuotePost,
        setMediaFiles, setMediaPreview, setIsReplyMode, setReplyToPost,
      })}

      {useDesktopComposer({
        showComposer, setShowComposer, text, setText,
        mediaFiles, mediaPreview, isPosting, handleMediaSelect,
        handlePost, handleSendComment, isReplyMode, replyToPost,
        quotePost, setQuotePost, setMediaFiles, setMediaPreview,
        setIsReplyMode, setReplyToPost,
      })}

      {useZoomModal({ zoomOpen, setZoomOpen, zoomSrc, zoomType })}

    {useBottomSheet({
  menu, setMenu, users, onRepost, myChatId,
  myUserName,          // ✅ NEW
  myDisplayName,       // ✅ NEW
  setQuotePost, setShowComposer, setIsReplyMode, setReplyToPost,
  onDeletePost,
  onEditPost: (post) => {
    setEditingPost(post);
    setText(post.text || "");
    setShowComposer(true);
  },
  onReportPost,
  followUser:   feedSocket?.followUser,
  unfollowUser: feedSocket?.unfollowUser,
})}

           {/* ── SUPPORT POST MODAL ─────────────────────────────────────────────── */}
      {supportModal.open && supportModal.post && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={handleCloseSupportModal}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                Support this post
              </span>
              <button
                onClick={handleCloseSupportModal}
                disabled={supportLoading}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-40"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
<div className="px-5 pb-6">
              {supportResult ? (
                /* ── SUCCESS / RECEIPT VIEW ───────────────────────────── */
                <div className="text-center">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                    Your support was sent successfully.
                  </div>
                  <div className="rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50 divide-y divide-neutral-200 dark:divide-neutral-600 mb-4 text-sm overflow-hidden">
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                      <span className="font-medium text-neutral-800 dark:text-neutral-100">
                        {Number(supportResult.amount).toFixed(2)} USWD
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
                      <span className="font-medium text-neutral-800 dark:text-neutral-100">
                        {Number(supportResult.fee).toFixed(2)} USWD
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-700 dark:text-neutral-200 font-semibold">Total</span>
                      <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                        {Number(supportResult.total).toFixed(2)} USWD
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseSupportModal}
                    className="w-full py-2.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white font-semibold text-sm transition-colors touch-manipulation"
                  >
                    Done
                  </button>
                </div>
              ) : (
                /* ── INPUT FORM ───────────────────────────────────────── */
                <>
                  {/* Post preview */}
                  <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl border border-neutral-200 dark:border-neutral-600 px-3 py-2.5 mb-4 text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">
                    <span className="font-medium text-neutral-800 dark:text-neutral-100">
                      @{supportModal.post.author?.username}
                    </span>
                    {supportModal.post.text ? (
                      <span> {supportModal.post.text}</span>
                    ) : (
                      <span className="italic text-neutral-400"> (media post)</span>
                    )}
                  </div>

                  {/* Amount label */}
                  <label
                    htmlFor="support-amount-input"
                    className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5"
                  >
                    Amount (USWD)
                  </label>

                  {/* Amount row */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg px-3 py-2 select-none whitespace-nowrap">
                      USWD
                    </span>
                    <input
                      id="support-amount-input"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      value={supportAmount}
                      onChange={(e) => {
                        setSupportAmount(e.target.value);
                        setSupportError("");
                      }}
                      disabled={supportLoading}
                      className="flex-1 bg-transparent border border-neutral-200 dark:border-neutral-600 focus:border-blue-400 dark:focus:border-blue-500 rounded-xl px-3 py-2 text-base outline-none focus:ring-2 focus:ring-blue-400/30 dark:focus:ring-blue-500/30 transition-colors disabled:opacity-50"
                      autoFocus
                    />
                  </div>

                   {/* Quick-select presets */}
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {[1, 5, 10, 25, 50].map((v) => (
                      <button
                        key={v}
                        onClick={() => { setSupportAmount(String(v)); setSupportError(""); }}
                        disabled={supportLoading}
                        className="text-xs px-3 py-1 rounded-full border border-neutral-200 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-40 touch-manipulation"
                      >
                        {v}
                      </button>
                    ))}
                  </div>

                  {/* Amount / fee / total preview */}
                  {parseFloat(supportAmount) > 0 && (
                    <div className="rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50 divide-y divide-neutral-200 dark:divide-neutral-600 mb-3 text-sm overflow-hidden">
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                        <span className="font-medium text-neutral-800 dark:text-neutral-100">
                          {parseFloat(supportAmount).toFixed(2)} USWD
                        </span>
                      </div>
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
                        <span className="font-medium text-neutral-800 dark:text-neutral-100">
                          {NETWORK_FEE_USWD.toFixed(2)} USWD
                        </span>
                      </div>
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-700 dark:text-neutral-200 font-semibold">Total</span>
                        <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                          {(parseFloat(supportAmount) + NETWORK_FEE_USWD).toFixed(2)} USWD
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {supportError && (
                    <p className="text-xs text-red-500 dark:text-red-400 mb-3 -mt-1">
                      {supportError}
                    </p>
                  )}

                  {/* Submit button */}
                  <button
                    onClick={handleSupportSubmit}
                    disabled={supportLoading || !supportAmount || parseFloat(supportAmount) <= 0}
                    className="w-full py-2.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors touch-manipulation"
                  >
                    {supportLoading ? "Sending…" : "Send support"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

{/* ── EARNINGS MODAL (post author — read-only) ────────────────────────── */}
      {earningsModal.open && earningsModal.post && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={handleCloseEarningsModal}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                Post earnings
              </span>
              <button
                onClick={handleCloseEarningsModal}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="px-5 pb-6">
              {/* Post preview */}
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl border border-neutral-200 dark:border-neutral-600 px-3 py-2.5 mb-4 text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">
                {earningsModal.post.text ? (
                  <span>{earningsModal.post.text}</span>
                ) : (
                  <span className="italic text-neutral-400">(media post)</span>
                )}
              </div>

              {/* Received for this post */}
              <div>
                <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Received for this post
                </div>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50">
                  <span className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
                    {(earningsModal.post.supportsAmount ?? earningsModal.post.supportReceived ?? 0).toFixed(2)}
                  </span>
                  <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">USWD</span>
                </div>
               
              </div>
            </div>
          </div>
        </div>
           )}

           <UserInfoOverlay
  open={showProfile}
  onClose={() => setShowProfile(false)}
  user={{ ...currentUser, chatId: myChatId }}
  myChatId={myChatId}
  mutualGroups={(groups || []).filter((g) => {
    const mem = groupMembers?.[g.groupId] || g.members || [];
    return Array.isArray(mem) && mem.some((m) => String(m.chatId) === String(myChatId));
  })}
  uploadImage={uploadImage}
  onOpenGroup={(g) => {
    setShowProfile(false);
    setActiveTab?.(T.CHATS);
  }}
/>

      <UserInfoOverlay
        open={!!selectedUserId}
        onClose={() => setSelectedUserId(null)}
        user={users.find((u) => String(u.chatId) === String(selectedUserId))}
        mutualGroups={
          (groups || []).filter((g) => {
            const mem = groupMembers?.[g.groupId] || g.members || [];
            return (
              Array.isArray(mem) &&
              mem.some((m) => String(m.chatId) === String(myChatId)) &&
              mem.some((m) => String(m.chatId) === String(selectedUserId))
            );
          })
        }
        uploadImage={uploadImage}
        socket={socket}
        myChatId={myChatId}
        isFollowing={
          users.find((u) => String(u.chatId) === String(selectedUserId))
            ?.isFollowing
        }
        onFollow={() => onFollow?.(selectedUserId)}
        onUnfollow={() => onUnfollow?.(selectedUserId)}
        followersCount={
          users.find((u) => String(u.chatId) === String(selectedUserId))
            ?.followersCount || 0
        }
        followingCount={
          users.find((u) => String(u.chatId) === String(selectedUserId))
            ?.followingCount || 0
        }
    
      />
          {showIOSInstallHelp && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={() => setShowIOSInstallHelp(false)}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                Install app
              </span>
              <button
                onClick={() => setShowIOSInstallHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-5 pb-6 text-sm text-neutral-600 dark:text-neutral-300 space-y-2">
              <p>To install this app on your iPhone:</p>
              <p>1. Tap the <strong>Share</strong> icon <span aria-hidden="true">⬆️</span> in Safari's toolbar.</p>
              <p>2. Scroll down and tap <strong>Add to Home Screen</strong>.</p>
              <p>3. Tap <strong>Add</strong> in the top right corner.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}